/*
 * The site's name and face, held once for the whole application.
 *
 * Both shells render the same brand in the same corner - the public header and
 * the admin header - and the Settings page changes it. Without one holder, saving
 * a new title would update the page that saved it and nothing else until a
 * reload; with one, the admin header changes as the save returns, and the public
 * shell reads the same object.
 *
 * WHERE THE VALUE COMES FROM, IN ORDER
 *
 *   1. The public bootstrap. PublicLayout fetches /public/bootstrap for its
 *      filters anyway and that payload carries `site`, so a public page load costs
 *      no extra request; the layout hands the value in through setSite.
 *   2. /public/site, on demand. The admin shell has no bootstrap, so it asks the
 *      provider to load - one small GET, served no-store - the first time it
 *      renders with nothing loaded.
 *   3. The Settings page. It loads the admin row (no-store) and gets the whole row
 *      back from every write, and pushes both in as AUTHORITATIVE.
 *
 * AUTHORITATIVE VALUES ARE NEVER OVERWRITTEN BY LOADED ONES. The bootstrap is a
 * cached public response - a minute of max-age by default - so an administrator
 * who saves a new name and then clicks through to the public site would have the
 * layout hand a stale bootstrap to the provider and watch their own change revert
 * in the header. A value that came from the admin row is the truth; a value that
 * came from a cache is a guess about the truth. So setSite records which kind it
 * was given, and a plain load cannot replace an authoritative value. Loads can
 * replace loads: a fresh public page load still refreshes a name changed in some
 * other tab.
 *
 * Until anything has arrived, `site` is null and the brand falls back to the
 * generic "Knowledge Base" it always showed while loading. It never falls back to
 * a hard-coded product name: the whole point of the setting is that the name is
 * not ours to hard-code.
 *
 * THE ICON URL CARRIES THE CONTENT HASH. The icon endpoint sends a day of
 * max-age when the hash in the URL matches the stored icon, so the URL has to
 * change when the bytes do; siteIconUrl puts the hash in the query string, the
 * browser sees a new address and fetches. If the image nonetheless fails to load -
 * a bootstrap cached before the icon was removed still names it, and the endpoint
 * now answers 404 - the brand falls back to the built-in mark rather than showing
 * an empty box.
 *
 * THE THEME IS APPLIED HERE TOO, as data-theme on <html>, because it is part of
 * the same row and the stylesheet keys every accent token on that attribute. A
 * PREVIEW is a second value the Settings page can set that overrides the saved
 * theme on this page only - it never leaves the browser, and the page that set
 * it clears it when it unmounts. Applying a theme is an ordinary save: the row
 * comes back with the new name, the provider applies it, the preview is dropped.
 *
 * THE FIRST PAINT. The row arrives with the bootstrap, after the first render,
 * and until then no attribute means Classic - so a site on any other theme would
 * flash blue on every load. The provider therefore remembers the SAVED theme
 * (never a preview) in localStorage, and main.tsx applies that memory before
 * React renders; the row corrects it moments later if it has changed. A browser
 * without storage simply gets the flash.
 *
 * THE BROWSER TAB'S ICON IS THE SAME ICON (2026-09-22). index.html ships
 * `<link rel="icon" href="data:,">` - no icon, and no request for /favicon.ico
 * either - and the provider repoints that link at the icon URL above once the
 * row is known, type included, so the tab and the header share one download and
 * one cache entry. It goes back to the empty URI when the row has no icon, and
 * when the header's image fails to load (the stale-bootstrap case above), since a
 * picture the header cannot draw is not one the tab should keep asking for. It
 * has the theme's first-paint problem and gets the theme's answer: the last
 * known icon is remembered in localStorage and main.tsx puts it in the tab before
 * React renders; the row corrects it moments later. Clients that never read the
 * page - bookmark tools, link previews - get the icon from the server's
 * /favicon.ico redirect instead (packages/server/src/favicon.ts).
 */

const THEME_STORAGE_KEY = 'kb.theme';
const ICON_STORAGE_KEY = 'kb.siteIcon';
/** What index.html ships: no icon, and no request for /favicon.ico either. */
const EMPTY_FAVICON = 'data:,';

/**
 * Put the last saved theme on <html> before the first render. Called once from
 * main.tsx; a value that is not a theme name is ignored, and storage that throws
 * (private mode, disabled) is ignored too.
 */
export function applyRememberedTheme(): void {
  try {
    const remembered = localStorage.getItem(THEME_STORAGE_KEY);
    if (remembered && isSiteTheme(remembered)) document.documentElement.dataset.theme = remembered;
  } catch {
    // No storage: the first paint is Classic until the row arrives.
  }
}

/** The identity the tab needs: which bytes, and what kind. */
type RememberedIcon = Pick<SiteIcon, 'contentType' | 'contentHash'>;

/** The <link rel="icon"> index.html ships - created if a document lacks one. */
function faviconLink(): HTMLLinkElement {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing) return existing;
  const link = document.createElement('link');
  link.rel = 'icon';
  document.head.appendChild(link);
  return link;
}

/**
 * Point the browser tab at the site icon, or back at nothing. The URL is the one
 * the header mark loads, hash included, so the tab shares that download and the
 * cache entry behind it; the type lets a browser that does not draw a format
 * skip it without fetching first.
 */
export function applySiteIconAsFavicon(icon: RememberedIcon | null): void {
  const link = faviconLink();
  if (icon) {
    link.setAttribute('href', siteIconUrl(icon.contentHash));
    link.setAttribute('type', icon.contentType);
  } else {
    link.setAttribute('href', EMPTY_FAVICON);
    link.removeAttribute('type');
  }
}

/**
 * What the last load left behind, if it is exactly the record this module
 * writes: a known content type and a whole lowercase SHA-256. Anything else -
 * another origin's leftovers, an edit by hand, an older shape - is ignored rather
 * than put in a URL.
 */
function readRememberedIcon(): RememberedIcon | null {
  const raw = localStorage.getItem(ICON_STORAGE_KEY);
  if (!raw) return null;
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { contentType, contentHash } = parsed as Record<string, unknown>;
  if (typeof contentType !== 'string' || !isArticleImageContentType(contentType)) return null;
  if (typeof contentHash !== 'string' || !/^[0-9a-f]{64}$/.test(contentHash)) return null;
  return { contentType, contentHash };
}

/**
 * Put the last known icon in the tab before the first render. Called once from
 * main.tsx, beside applyRememberedTheme and for the same reason: the row arrives
 * after the first paint, and a tab that shows nothing and then the icon on every
 * load is the flash the theme used to have. A malformed record is ignored, and
 * storage that throws is ignored too.
 */
export function applyRememberedSiteIcon(): void {
  try {
    const remembered = readRememberedIcon();
    if (remembered) applySiteIconAsFavicon(remembered);
  } catch {
    // No storage, or not a record this module wrote: the tab waits for the row.
  }
}

function rememberIcon(icon: RememberedIcon | null): void {
  try {
    if (icon) {
      localStorage.setItem(
        ICON_STORAGE_KEY,
        JSON.stringify({ contentType: icon.contentType, contentHash: icon.contentHash }),
      );
    } else {
      localStorage.removeItem(ICON_STORAGE_KEY);
    }
  } catch {
    // No storage: nothing to remember with.
  }
}

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PublicSiteSettings, SiteIcon, SiteTheme } from '@accotool-kb/shared';
import {
  DEFAULT_SITE_THEME,
  isArticleImageContentType,
  isSiteTheme,
  siteIconUrl,
} from '@accotool-kb/shared';
import { getSiteSettings } from '../api/publicApi.js';

/** What the header says before anything has loaded. Deliberately generic. */
export const FALLBACK_SITE_TITLE = 'Knowledge Base';

export interface SiteBrandingValue {
  /** Null until loaded; the brand shows the fallback title and the built-in mark. */
  site: PublicSiteSettings | null;
  /**
   * The icon to draw, if any: the row's icon unless its image has failed to load,
   * in which case null - the built-in mark for the header, the empty URI for the
   * tab. A consumer that draws the icon reports a failure through markIconBroken,
   * so the header and the tab follow one verdict.
   */
  icon: SiteIcon | null;
  markIconBroken: (contentHash: string) => void;
  /**
   * Hand in a value. `authoritative` marks it as coming from the admin row (a
   * load of it, or a write's response); a plain value is a public, possibly cached
   * one and never replaces an authoritative one.
   */
  setSite: (next: PublicSiteSettings, options?: { authoritative?: boolean }) => void;
  /** Fetch /public/site if nothing is loaded yet. Idempotent; safe to call every render. */
  ensureLoaded: () => void;
  /** A theme shown on THIS page only, over the saved one. Null when not previewing. */
  previewTheme: SiteTheme | null;
  setPreviewTheme: (theme: SiteTheme | null) => void;
}

interface Held {
  site: PublicSiteSettings | null;
  authoritative: boolean;
}

const SiteBrandingContext = createContext<SiteBrandingValue | null>(null);

export function SiteBrandingProvider({
  children,
  initial = null,
}: {
  children: ReactNode;
  /** For tests, and for a shell that already has the value. */
  initial?: PublicSiteSettings | null;
}): JSX.Element {
  const [held, setHeld] = useState<Held>({ site: initial, authoritative: false });
  const [previewTheme, setPreviewThemeState] = useState<SiteTheme | null>(null);
  const setPreviewTheme = useCallback((theme: SiteTheme | null) => setPreviewThemeState(theme), []);

  // Refs rather than state for the two things the callbacks read: a re-created
  // callback would change the context value's identity, and every consumer's
  // effect keyed on it would run again for nothing.
  const heldRef = useRef(held);
  heldRef.current = held;
  const loading = useRef(false);

  const setSite = useCallback<SiteBrandingValue['setSite']>((next, options) => {
    const authoritative = options?.authoritative === true;
    setHeld((current) =>
      current.authoritative && !authoritative ? current : { site: next, authoritative },
    );
  }, []);

  const ensureLoaded = useCallback(() => {
    if (heldRef.current.site !== null || loading.current) return;
    loading.current = true;
    getSiteSettings()
      // A value handed in while the load was in flight wins: the bootstrap, or a
      // save, is never older than what this fetch brings back.
      .then((loaded) =>
        setHeld((current) => (current.site ? current : { site: loaded, authoritative: false })),
      )
      // A failed load is not worth an error anywhere: the brand keeps its
      // fallback and the next mount tries again.
      .catch(() => undefined)
      .finally(() => {
        loading.current = false;
      });
  }, []);

  // The browser tab. One place, so a page that sets a more specific title can
  // still read the site name from here rather than from a constant.
  useEffect(() => {
    document.title = held.site?.title ?? FALLBACK_SITE_TITLE;
  }, [held.site?.title]);

  // The hash whose image failed to load, if any. Keyed on the hash rather than a
  // boolean so a NEW icon gets its chance even after an old one failed. Held here
  // rather than in SiteBrand because the tab's icon follows the same verdict.
  const [brokenHash, setBrokenHash] = useState<string | null>(null);
  const markIconBroken = useCallback((contentHash: string) => setBrokenHash(contentHash), []);
  const icon =
    held.site?.icon && held.site.icon.contentHash !== brokenHash ? held.site.icon : null;

  // The tab's icon, and the memory of it for the next load's first paint. Nothing
  // happens until the row has arrived: before that the link holds whatever
  // main.tsx remembered, and clearing it would be the flash this exists to avoid.
  const loaded = held.site !== null;
  const iconHash = icon?.contentHash ?? null;
  const iconType = icon?.contentType ?? null;
  useEffect(() => {
    if (!loaded) return;
    const current = iconHash && iconType ? { contentHash: iconHash, contentType: iconType } : null;
    applySiteIconAsFavicon(current);
    rememberIcon(current);
  }, [loaded, iconHash, iconType]);

  // The theme, as an attribute on <html>: every accent token in the stylesheet is
  // keyed on it. A preview wins over the saved theme while it lasts; before
  // anything has loaded the default applies, which is also what no attribute
  // would have meant.
  const appliedTheme = previewTheme ?? held.site?.theme ?? DEFAULT_SITE_THEME;
  useEffect(() => {
    document.documentElement.dataset.theme = appliedTheme;
  }, [appliedTheme]);

  // Remember the SAVED theme for the next load's first paint - never a preview.
  const savedTheme = held.site?.theme;
  useEffect(() => {
    if (!savedTheme) return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, savedTheme);
    } catch {
      // No storage: nothing to remember with.
    }
  }, [savedTheme]);

  const value = useMemo<SiteBrandingValue>(
    () => ({
      site: held.site,
      icon,
      markIconBroken,
      setSite,
      ensureLoaded,
      previewTheme,
      setPreviewTheme,
    }),
    [held.site, icon, markIconBroken, setSite, ensureLoaded, previewTheme, setPreviewTheme],
  );

  return <SiteBrandingContext.Provider value={value}>{children}</SiteBrandingContext.Provider>;
}

export function useSiteBranding(): SiteBrandingValue {
  const context = useContext(SiteBrandingContext);
  if (!context) throw new Error('useSiteBranding must be used inside SiteBrandingProvider.');
  return context;
}

/**
 * The mark and the name, as they appear top-left in both shells.
 *
 * The uploaded icon replaces the built-in "KB" square when there is one; the
 * square is the fallback and never shows beside the icon. alt="" on the image
 * because the title text is right beside it - a screen reader announcing "logo"
 * and then the name would only say it twice.
 */
export function SiteBrand(): JSX.Element {
  const { site, icon, markIconBroken } = useSiteBranding();
  const title = site?.title ?? FALLBACK_SITE_TITLE;

  return (
    <>
      {icon ? (
        <img
          className="brand-icon"
          src={siteIconUrl(icon.contentHash)}
          alt=""
          onError={() => markIconBroken(icon.contentHash)}
        />
      ) : (
        <span className="brand-mark" aria-hidden="true">
          KB
        </span>
      )}
      {title}
    </>
  );
}
